import {
  createFleetFuelCardAssignmentRequestSchema,
  createFleetFuelCardRequestSchema,
  fleetCostMonthListSchema,
  fleetCostMonthQuerySchema,
  fleetFuelCardListSchema,
  fleetFuelCardSchema,
  fleetFuelCardAssignmentSchema,
  fleetFuelImportResultSchema,
  fleetFuelIssuerSchema,
  fleetFuelTransactionListQuerySchema,
  fleetFuelTransactionListSchema,
  fleetFuelTransactionSchema,
  fleetVehicleCostDetailSchema,
  fleetVehicleCostQuerySchema,
  resolveFleetFuelTransactionRequestSchema,
  updateFleetFuelCardRequestSchema,
  type CreateFleetFuelCardAssignmentRequest,
  apiErrorSchema,
  type CreateFleetFuelCardRequest,
  type FleetFuelIssuer,
  type FleetFuelTransactionListQuery,
  type FleetVehicleCostQuery,
  type ResolveFleetFuelTransactionRequest,
  type UpdateFleetFuelCardRequest,
} from '@gb-ticket/shared-types';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { API_BASE_URL, ApiRequestError, apiFetch } from '@/lib/api';

export const fleetFuelCardsKey = ['fleet', 'fuel', 'cards'] as const;
export const fleetFuelTransactionsKey = (query?: FleetFuelTransactionListQuery) =>
  ['fleet', 'fuel', 'transactions', query ?? {}] as const;
export const fleetCostMonthKey = (month: string) => ['fleet', 'costs', month] as const;
export const fleetVehicleCostsKey = (vehicleId: string, query: FleetVehicleCostQuery) =>
  ['fleet', 'vehicle', vehicleId, 'costs', query] as const;

export function useFleetFuelCards(enabled = true) {
  return useQuery({
    queryKey: fleetFuelCardsKey,
    queryFn: () => apiFetch('/fleet/fuel/cards', fleetFuelCardListSchema),
    enabled,
  });
}

export function useCreateFleetFuelCard() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (body: CreateFleetFuelCardRequest) => {
      const parsed = createFleetFuelCardRequestSchema.parse(body);
      return apiFetch('/fleet/fuel/cards', fleetFuelCardSchema, {
        method: 'POST',
        body: JSON.stringify(parsed),
      });
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: fleetFuelCardsKey });
    },
  });
}

export function useUpdateFleetFuelCard() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ cardId, body }: { cardId: string; body: UpdateFleetFuelCardRequest }) => {
      const parsed = updateFleetFuelCardRequestSchema.parse(body);
      return apiFetch(`/fleet/fuel/cards/${cardId}`, fleetFuelCardSchema, {
        method: 'PATCH',
        body: JSON.stringify(parsed),
      });
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: fleetFuelCardsKey });
    },
  });
}

export function useCreateFleetFuelCardAssignment() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      cardId,
      body,
    }: {
      cardId: string;
      body: CreateFleetFuelCardAssignmentRequest;
    }) => {
      const parsed = createFleetFuelCardAssignmentRequestSchema.parse(body);
      return apiFetch(`/fleet/fuel/cards/${cardId}/assignments`, fleetFuelCardAssignmentSchema, {
        method: 'POST',
        body: JSON.stringify(parsed),
      });
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: fleetFuelCardsKey });
      void queryClient.invalidateQueries({ queryKey: ['fleet', 'fuel', 'transactions'] });
    },
  });
}

export function useImportFleetFuelCsv() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ issuer, file }: { issuer: FleetFuelIssuer; file: File }) => {
      const parsedIssuer = fleetFuelIssuerSchema.parse(issuer);
      const form = new FormData();
      form.append('issuer', parsedIssuer);
      form.append('file', file);
      const response = await fetch(`${API_BASE_URL}/fleet/fuel/import`, {
        method: 'POST',
        body: form,
        credentials: 'include',
      });
      const body: unknown = await response.json().catch(() => undefined);
      if (!response.ok) {
        const parsed = apiErrorSchema.safeParse(body);
        throw new ApiRequestError(
          parsed.success
            ? parsed.data
            : {
                statusCode: response.status,
                code: 'UNKNOWN',
                message: 'Der Import ist fehlgeschlagen.',
                timestamp: new Date().toISOString(),
                path: '/fleet/fuel/import',
              },
        );
      }
      return fleetFuelImportResultSchema.parse(body);
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: fleetFuelCardsKey });
      void queryClient.invalidateQueries({ queryKey: ['fleet', 'fuel', 'transactions'] });
      void queryClient.invalidateQueries({ queryKey: ['fleet', 'costs'] });
      void queryClient.invalidateQueries({ queryKey: ['fleet', 'summary'] });
    },
  });
}

export function useFleetFuelTransactions(query: FleetFuelTransactionListQuery, enabled = true) {
  const parsed = fleetFuelTransactionListQuerySchema.parse(query);
  const params = new URLSearchParams();
  if (parsed.month) {
    params.set('month', parsed.month);
  }
  if (parsed.status) {
    params.set('status', parsed.status);
  }
  if (parsed.vehicleId) {
    params.set('vehicleId', parsed.vehicleId);
  }
  const qs = params.toString();
  return useQuery({
    queryKey: fleetFuelTransactionsKey(parsed),
    queryFn: () =>
      apiFetch(`/fleet/fuel/transactions${qs ? `?${qs}` : ''}`, fleetFuelTransactionListSchema),
    enabled,
  });
}

export function useResolveFleetFuelTransaction() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      transactionId,
      body,
    }: {
      transactionId: string;
      body: ResolveFleetFuelTransactionRequest;
    }) => {
      const parsed = resolveFleetFuelTransactionRequestSchema.parse(body);
      return apiFetch(
        `/fleet/fuel/transactions/${transactionId}/resolve`,
        fleetFuelTransactionSchema,
        {
          method: 'POST',
          body: JSON.stringify(parsed),
        },
      );
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['fleet', 'fuel', 'transactions'] });
      void queryClient.invalidateQueries({ queryKey: ['fleet', 'summary'] });
      void queryClient.invalidateQueries({ queryKey: ['fleet', 'tasks'] });
    },
  });
}

export function useFleetCostMonth(month: string, enabled = true) {
  const parsed = fleetCostMonthQuerySchema.parse({ month });
  return useQuery({
    queryKey: fleetCostMonthKey(parsed.month),
    queryFn: () =>
      apiFetch(`/fleet/costs?month=${encodeURIComponent(parsed.month)}`, fleetCostMonthListSchema),
    enabled,
  });
}

export function useFleetVehicleCosts(
  vehicleId: string,
  query: FleetVehicleCostQuery,
  enabled = true,
) {
  const parsed = fleetVehicleCostQuerySchema.parse(query);
  const params = new URLSearchParams({ from: parsed.from, to: parsed.to });
  return useQuery({
    queryKey: fleetVehicleCostsKey(vehicleId, parsed),
    queryFn: () =>
      apiFetch(
        `/fleet/vehicles/${vehicleId}/costs?${params.toString()}`,
        fleetVehicleCostDetailSchema,
      ),
    enabled: enabled && Boolean(vehicleId),
  });
}
